import { RECENTS_KEY, migrateFavoritesToNaptan, type FavoritesService } from '@metro/suggester'
import type { SchemaData } from '../schema/types'
import { mapAssetUrl } from '../schema/mapSet'
import * as routeSelection from './routeSelectionPersistence'
import {
  loadHistory,
  loadSavedRoutes,
  saveHistory,
  saveSavedRoutes,
} from './routeLibraryPersistence'
import { makeRouteLibraryIdRewrite } from './routeLibrary/routeLibraryIdRewrite'

/**
 * The city's v1 → v2 id table, bundled with the map set as
 * `/map-v2/legacy-ids.json` (metro-server `cities/london/legacy_ids.json`):
 * v1 line id → v2 line id, v1 station record id → v2 stop token — the
 * NaPTAN, plus `-<platform>` for platform twins (`940GZZLULYS-0`) — a v1
 * slug no v2 station serves → its station's v2 slug, and a v1 station NaPTAN
 * no v2 station has → its station's v2 id (Bushey `910GBUSHEY` →
 * `910GBUSHYDC`). The
 * `GET /api/v2/migration/legacy-ids` body is the same table with the
 * platform dropped ({@link stationOfToken}).
 */
export interface LegacyIdTable {
  readonly lines: Readonly<Record<string, string>>
  readonly stations: Readonly<Record<string, string>>
  readonly slugs?: Readonly<Record<string, string>>
  readonly naptans?: Readonly<Record<string, string>>
}

/** `localStorage` flag: the stored v1 ids were rewritten (TASK-986). */
export const LEGACY_IDS_MIGRATED_KEY = 'metro:v2:legacyIdsMigrated' as const

/** `station-{line}-{code}` — the v1 record id shape this module exists to read. */
const V1_STATION_ID = /^station-([^-]+)-/

/** Station id of a stop token: `940GZZLULYS-0` → `940GZZLULYS` (server `model.StationOf`). */
export function stationOfToken(token: string): string {
  return /-[0-9]$/.test(token) && token.length > 2 ? token.slice(0, -2) : token
}

/**
 * v1 station record id → v2 stop id: the exact stop the table names on the
 * record's line (`central.940GZZLULYS-0`), else that line's first stop at the
 * station, else the station's first stop (by id). An id that already names a
 * v2 stop is kept; anything the table or the map does not know → undefined
 * (dropped by the callers — nothing could show it).
 */
export function buildLegacyStopResolver(
  table: LegacyIdTable,
  schema: SchemaData,
): (id: string) => string | undefined {
  const stopIds = new Set(schema.stations.map((s) => s.id))
  const stopsByStation = new Map<string, { id: string; lineId: string }[]>()
  for (const s of schema.stations) {
    if (s.naptanId === undefined) continue
    const list = stopsByStation.get(s.naptanId) ?? []
    list.push({ id: s.id, lineId: s.line.id })
    stopsByStation.set(s.naptanId, list)
  }
  for (const list of stopsByStation.values()) list.sort((a, b) => a.id.localeCompare(b.id))
  return (id) => {
    if (stopIds.has(id)) return id
    const token = table.stations[id]
    if (token === undefined) return undefined
    const segment = V1_STATION_ID.exec(id)?.[1]
    const line = segment === undefined ? undefined : table.lines[`line-${segment}`]
    if (line !== undefined && stopIds.has(`${line}.${token}`)) return `${line}.${token}`
    const stops = stopsByStation.get(stationOfToken(token))
    if (stops === undefined || stops.length === 0) return undefined
    return (stops.find((s) => s.lineId === line) ?? stops[0]).id
  }
}

/**
 * One-shot rewrite of the ids a pre-v2 web client stored (TASK-986):
 * Suggester recents (v1 record ids → v2 stop ids) and favourites still on v1
 * record ids (→ NaPTAN, the v2 station id); a favourite on one of the five
 * NaPTANs v2 re-keyed moves to the station's new id, in place. Route selections and the route
 * library key on slugs, three of which v2 retired, and history snapshots name
 * v1 lines: both are rewritten. A stale routing variant signature falls back
 * to the first variant by itself.
 *
 * Idempotent through {@link LEGACY_IDS_MIGRATED_KEY}; without working
 * storage it does nothing (there is nothing stored to rewrite).
 */
export function migrateStoredIdsToV2(
  table: LegacyIdTable,
  schema: SchemaData,
  favorites: FavoritesService,
): void {
  try {
    if (localStorage.getItem(LEGACY_IDS_MIGRATED_KEY) === '1') return
  } catch {
    return
  }
  const resolve = buildLegacyStopResolver(table, schema)
  try {
    const raw = localStorage.getItem(RECENTS_KEY)
    const stored: unknown = raw === null ? [] : JSON.parse(raw)
    if (Array.isArray(stored)) {
      const recents: string[] = []
      for (const id of stored) {
        const stop = typeof id === 'string' ? resolve(id) : undefined
        if (stop !== undefined && !recents.includes(stop)) recents.push(stop)
      }
      localStorage.setItem(RECENTS_KEY, JSON.stringify(recents))
    }
  } catch {
    // Unreadable recents: the Suggester treats them as empty anyway.
  }
  // Favourites not yet on NaPTAN ids: the v1 record id resolves through
  // the table (the v2 map no longer carries v1 ids).
  migrateFavoritesToNaptan(favorites, (id) => {
    const token = table.stations[id]
    return token === undefined ? undefined : stationOfToken(token)
  })
  rewriteFavorites(favorites, (id) => table.naptans?.[id] ?? id)
  const slug = (s: string): string => table.slugs?.[s] ?? s
  const selection = routeSelection.load()
  if (selection !== null) {
    routeSelection.save({
      lastFromStationSlug:
        selection.lastFromStationSlug === null ? null : slug(selection.lastFromStationSlug),
      recentStationSlugs: [...new Set(selection.recentStationSlugs.map(slug))],
    })
  }
  const library = makeRouteLibraryIdRewrite(slug, (id) => table.lines[id] ?? id)
  saveHistory(library.history(loadHistory()))
  saveSavedRoutes(library.saved(loadSavedRoutes()))
  try {
    localStorage.setItem(LEGACY_IDS_MIGRATED_KEY, '1')
  } catch {
    // Not recorded: the next load repeats an idempotent pass.
  }
}

/**
 * Loads the bundled id table and runs {@link migrateStoredIdsToV2} — only on
 * a store not migrated yet, so the table is fetched once per browser. A
 * failed fetch leaves the store untouched and the next load retries.
 */
export async function runLegacyIdMigration(
  schema: SchemaData,
  favorites: FavoritesService,
  fetchTable: () => Promise<LegacyIdTable> = fetchLegacyIdTable,
): Promise<void> {
  try {
    if (localStorage.getItem(LEGACY_IDS_MIGRATED_KEY) === '1') return
  } catch {
    return
  }
  let table: LegacyIdTable
  try {
    table = await fetchTable()
  } catch {
    return
  }
  migrateStoredIdsToV2(table, schema, favorites)
}

async function fetchLegacyIdTable(): Promise<LegacyIdTable> {
  const res = await fetch(mapAssetUrl('legacy-ids.json'))
  if (!res.ok) throw new Error(`legacy-ids.json: HTTP ${res.status}`)
  return (await res.json()) as LegacyIdTable
}

/** Rewrites every favourite through `rewrite`, keeping order; a station favourited twice collapses. */
function rewriteFavorites(favorites: FavoritesService, rewrite: (id: string) => string): void {
  const current = favorites.getAll()
  const next = [...new Set(current.map(rewrite))]
  if (next.length === current.length && next.every((id, i) => id === current[i])) return
  for (const id of current) favorites.unfavorite(id)
  for (const id of next) favorites.favorite(id)
}
